import { useProject } from '@/features/project/_hooks/project';
import { useCurrentFiscalYear } from '@/features/search/_hooks/get-current-fiscal-year';
import { useMemo } from 'react';
import {
  useAllConsecutiveIndicators,
  useIndicators,
} from '../../activities/_hooks/activity';

/**
 *
 * 事業年度（作成年度）が以下のいずれかに該当する場合はtrue
 * ①事業終了予定年度を過ぎた場合
 * ②短期アウトカムで設定した目標年度の翌年度
 * ③中期アウトカムで設定した目標年度の翌年度
 * ④長期アウトカムで設定した目標年度又は最終目標年度の翌年度
 */
export const useRequiredByEndYearOrOutcomeTargetYear = (projectId: string) => {
  const project = useProject(projectId);
  const currentFiscalYear = useCurrentFiscalYear();
  const fiscalYear =
    project.data?.fiscalYear ??
    currentFiscalYear.data ??
    currentFiscalYear.basicFiscalYear;
  const indicatorsData = useIndicators({
    filters: { keyId: projectId },
  });

  const indicatorIds = indicatorsData.data?.map((v) => v.id);
  const { data: consecutiveIndicators } = useAllConsecutiveIndicators(
    indicatorIds ?? [],
  );

  const hasRequiredOutcomeTargetYear = useMemo(
    () =>
      !!indicatorsData.data?.some((v) => {
        const isOutcome = v.type === 'outcome';
        if (!isOutcome || v.dataNatureType === 'qualitative') {
          return false;
        }

        const isLongTerm = v.term === 'long';
        const consecutive = consecutiveIndicators?.[v.id];
        const nextYearsOfTargetYear =
          consecutive?.flatMap((v) =>
            v.isTargetYear ? [v.fiscalYear + 1] : [],
          ) ?? [];

        const lastYear = consecutive?.at(-1)?.fiscalYear;
        const nextYearOfLastYear =
          isLongTerm && lastYear ? lastYear + 1 : undefined;

        const hasRequiredYear = [
          ...nextYearsOfTargetYear,
          nextYearOfLastYear,
        ].some((v) => v === fiscalYear);
        return hasRequiredYear;
      }),
    [indicatorsData.data, consecutiveIndicators, fiscalYear],
  );

  return {
    required:
      (project.data?.endYear &&
        fiscalYear &&
        project.data.endYear <= fiscalYear) ||
      hasRequiredOutcomeTargetYear,
    isLoading:
      project.isPending ||
      indicatorsData.isPending ||
      currentFiscalYear.isPending,
  };
};
